import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useBlogSettings, resolveBlogLang } from "@/context/BlogContext";
import { SEOHead } from "@/components/SEOHead";
import { BlogHero } from "@/components/blog/BlogHero";
import { FeaturedGrid } from "@/components/blog/FeaturedGrid";
import { BlogCard } from "@/components/blog/BlogCard";
import { BlogSidebar } from "@/components/blog/BlogSidebar";
import { Pagination } from "@/components/blog/Pagination";
import { blogFetch } from "@/lib/blog";

export function BlogListPage() {
  const { lang: uiLang, t } = useLanguage();
  const settings = useBlogSettings();
  const lang = resolveBlogLang(uiLang, settings);
  const [searchParams] = useSearchParams();

  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const search = searchParams.get("search") || "";
  const category = searchParams.get("category") || "";

  const [data, setData] = useState({ posts: [], total: 0, per_page: 12 });
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);

    Promise.all([
      blogFetch("", { lang, params: { page, per_page: 12, search, category }, signal: ctrl.signal }),
      page === 1 && !search && !category
        ? blogFetch("", { lang, params: { featured: 1, per_page: 3 }, signal: ctrl.signal })
        : Promise.resolve({ posts: [] }),
      blogFetch("/categories", { lang, signal: ctrl.signal }),
    ])
      .then(([listData, featuredData, catData]) => {
        setData(listData);
        setFeatured(featuredData.posts || []);
        setCategories(catData.categories || []);
        // Tüm yazıların etiketlerini topla (sidebar etiket bulutu için)
        const tagSet = new Set();
        (listData.posts || []).forEach((p) => (p.tags || []).forEach((tag) => tagSet.add(tag)));
        setTags(Array.from(tagSet).slice(0, 20));
        setLoading(false);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        setError(err.message);
        setLoading(false);
      });

    return () => ctrl.abort();
  }, [lang, page, search, category]);

  return (
    <>
      <SEOHead page="blog" />
      <BlogHero
        title={t("blog.heroTitle")}
        subtitle={t("blog.heroSubtitle")}
        categories={categories}
        activeCategorySlug={category || null}
        showSearch
        initialSearch={search}
      />

      {/* Featured (sadece ilk sayfa + filtresiz) */}
      {featured.length > 0 && <FeaturedGrid posts={featured} />}

      {/* Tüm yazılar grid + sidebar */}
      <section className="py-12 sm:py-16 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              {search
                ? `${t("blog.searchResults")}: "${search}"`
                : category
                ? t("blog.categoryResults")
                : t("blog.allPosts")}
            </h2>
            {data.total > 0 && (
              <span className="text-sm text-slate-500">
                {data.total} {t("blog.postsCount")}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              {loading && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 animate-pulse h-72" />
                  ))}
                </div>
              )}

              {error && !loading && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700 text-sm">
                  {t("blog.loadError")}
                </div>
              )}

              {!loading && !error && data.posts.length === 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
                  <p className="font-medium">{t("blog.empty")}</p>
                </div>
              )}

              {!loading && data.posts.length > 0 && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {data.posts.map((post) => (
                      <BlogCard key={post.id} post={post} lang={lang} />
                    ))}
                  </div>
                  <Pagination
                    page={page}
                    perPage={data.per_page}
                    total={data.total}
                    lang={lang}
                  />
                </>
              )}
            </div>

            <div className="lg:col-span-4">
              <BlogSidebar categories={categories} tags={tags} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
